/**
 * <s-sales-pop> — shows its [data-sales-pop-item] children one at a time in a
 * floating panel, then hides it and waits before showing the next.
 *
 * Attributes:
 *   data-interval — seconds between notifications (default 25)
 *   data-delay    — seconds before the first one (default 8)
 *   data-force    — editor preview: show the first item and keep it visible
 *
 * The X (command "--dismiss") hides the widget for the rest of the visit.
 */
const STORAGE_KEY = 'timexor-sales-pop-hidden';
const VISIBLE_MS = 6000;

class SSalesPop extends HTMLElement {
  #ac;
  #timer = 0;
  #index = -1;
  #paused = false;
  #panel = null;
  #items = [];

  connectedCallback() {
    this.#panel = this.querySelector('[data-sales-pop-panel]');
    this.#items = Array.from(this.querySelectorAll('[data-sales-pop-item]'));
    if (!this.#panel || this.#items.length === 0) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--dismiss') this.#dismiss();
    }, opts);
    // Direct listener too, so the X always closes even where command events are unavailable.
    this.querySelector('[data-sales-pop-close]')?.addEventListener('click', () => this.#dismiss(), opts);

    this.#panel.addEventListener('pointerenter', () => this.#pause(), opts);
    this.#panel.addEventListener('focusin', () => this.#pause(), opts);
    this.#panel.addEventListener('pointerleave', () => this.#resume(), opts);
    this.#panel.addEventListener('focusout', () => this.#resume(), opts);

    if (this.hasAttribute('data-force')) {
      this.#show(0);
      return;
    }
    if (this.#isHidden()) return;

    const delay = Math.max(0, Number(this.dataset.delay ?? 8)) * 1000;
    this.#schedule(() => this.#next(), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #schedule(fn, ms) {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(fn, ms);
  }

  #next() {
    this.#show((this.#index + 1) % this.#items.length);
    this.#schedule(() => this.#hideAndWait(), VISIBLE_MS);
  }

  #show(index) {
    this.#index = index;
    this.#items.forEach((item, i) => {
      item.hidden = i !== index;
    });
    this.#panel.toggleAttribute('data-open', true);
  }

  #hideAndWait() {
    if (this.#paused) return;
    this.#panel.removeAttribute('data-open');
    const interval = Math.max(5, Number(this.dataset.interval ?? 25)) * 1000;
    this.#schedule(() => this.#next(), interval);
  }

  #pause() {
    if (!this.#panel.hasAttribute('data-open') || this.hasAttribute('data-force')) return;
    this.#paused = true;
    window.clearTimeout(this.#timer);
  }

  #resume() {
    if (!this.#paused) return;
    if (this.#panel.matches(':hover, :focus-within')) return;
    this.#paused = false;
    this.#schedule(() => this.#hideAndWait(), 3000);
  }

  #dismiss() {
    window.clearTimeout(this.#timer);
    this.#paused = false;
    this.#panel.removeAttribute('data-open');
    this.#ac?.abort();
    if (this.hasAttribute('data-force')) return;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {}
  }

  #isHidden() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}

if (!customElements.get('s-sales-pop')) customElements.define('s-sales-pop', SSalesPop);
